import { getField, updateField } from 'vuex-map-fields'
import { formatDate } from '../utils'
import isObject from '@/utils/isObject'
import isObjectEmpty from '@/utils/isObjectEmpty'
import ApiService from '@/services/apiService'

const state = {
	aircraftCategory: 'helicopter',

	// Outbound
	date: '',
	time: '',
	timeIsArrival: false,
	// Return
	isReturn: false,
	returnDate: '',
	returnTime: '',
	returnTimeIsArrival: false,
	// Global journey
	passengers: 2,
	locations: {
		origin: {
			geoArea: {},
			is_private: false,
			value: null,
			transfer: {},
			transferType: null,
			options: {
				list: [],
			},
		},
		destination: {
			geoArea: {},
			is_private: false,
			value: null,
			transfer: {},
			transferType: null,
			options: {
				list: []
			},
		},
	},
	hoveredLocation: '',

	defaultDestinationLocation: false,

	// Stepper form
	stepper: {
		loading: false
	},

	// API stuff
	uuid: ''
}

const getters = {
	getField,

	aircraftCategory: (state) => state.aircraftCategory,

	// Outbound
	formattedDate: (state) => formatDate(state.date),
	formattedReturnDate: (state) => formatDate(state.returnDate),

	// Locations
	locationValue: (state) => (locationType) => state.locations[locationType].value,
	isLocationEmpty: (state) => (locationType) => !state.locations[locationType].value,
	defaultDestinationLocationSet: (state) =>  state.defaultDestinationLocation,

	// Get first location that has a value
	getFirstLocationValue: (state) => {
		let locationType = Object.keys(state.locations).find(type => state.locations[type].value)
		return locationType
			? state.locations[locationType].value
			: false
	},
	// Get location options by location type
	getLocationOptionsList: (state) => (locationType) => state.locations[locationType].options.list,
	// Get first location that has options
	getActiveLocationOptionsList: (state) => {
		let locationType = Object.keys(state.locations).find(type => state.locations[type].options.list.length >= 1)
		return locationType
			? state.locations[locationType].options.list
			: false
	},
	// Check if just one location is selected
	numberOfLocationsSelected: (state) => {
		let total = 0
		for (const location in state.locations) {
			if ({}.hasOwnProperty.call(state.locations, location)) {
				let loc = state.locations[location]
				if (isObject(loc.value) && !isObjectEmpty(loc.value)) total++
			}
		}
		return total
	},
	// Check if any location has active options dropdown
	hasActiveLocationOptions: (state, getters) => getters.getActiveLocationOptionsList !== false,
	// Get the currently hovered location option
	hoveredLocation: (state) => state.hoveredLocation,

	getTransferType: (state) => (locationType) => state.locations[locationType].transferType,

	// Location transfers
	getFormattedTransfer: (state, getters) => (locationType) => {
		let transfer = state.locations[locationType].transfer
		return (isObjectEmpty(transfer))
			? {}
			: {
				transfer_address: transfer.formatted_address,
				transfer_lat: transfer.geometry.location.lat(),
				transfer_lng: transfer.geometry.location.lng(),
				transfer_name: transfer.name,
				transfer_type_id: getters.getTransferType(locationType)
			}
	},

	// If the location selection is a 'location' (airfield or helipad)
	// return the ID, otherwise it's a geo area and null is returned
	getFormattedLocationID: (state) => (locationType) => {
		return (!state.locations[locationType].value?.isGeoArea)
			? state.locations[locationType].value.id
			: null
	},

	getFormattedIsPrivate: (state) => (locationType) => {
		return state.locations[locationType].value?.is_private
	},

	// If the location selected is a 'geo area' (no specific location chosen)
	// return a location object, otherwise it's a location and return null
	getFormattedGeoArea: (state) => (locationType) => {
		return (state.locations[locationType].value?.isGeoArea)
			? {
				name: state.locations[locationType].value.primary,
				address: state.locations[locationType].value.secondary,
				lat: state.locations[locationType].value.lat,
				lng: state.locations[locationType].value.lng
			}
			: null
	},

	// Journey
	getCompleteJourneyValues: (state) => [state.locations.origin.value, state.locations.destination.value],
	formattedJourney: (state, getters) => {
		console.log(getters.getFormattedIsPrivate('destination'));
		return [
			{
				location_id: getters.getFormattedLocationID('origin'),
				is_private: getters.getFormattedIsPrivate('origin'),
				private_address: getters.getFormattedIsPrivate('origin') ? {
					lat:  getters.locationValue('origin').lat,
					lng:  getters.locationValue('origin').lng,
					address:  getters.locationValue('origin').secondary,
					name:  getters.locationValue('origin').primary
				} : {},
				transfer: getters.getFormattedTransfer('origin'),
				geo_area: getters.getFormattedGeoArea('origin')
			},
			{
				location_id: getters.getFormattedLocationID('destination'),
				is_private: getters.getFormattedIsPrivate('destination'),
				blah: false,
				private_address: getters.getFormattedIsPrivate('destination') ? {
					lat:  getters.locationValue('destination').lat,
					lng:  getters.locationValue('destination').lng,
					address:  getters.locationValue('destination').secondary,
					name:  getters.locationValue('destination').primary
				} : {},
				transfer: getters.getFormattedTransfer('destination'),
				geo_area: getters.getFormattedGeoArea('destination')
			}
		]
	},
	// General
	loadingStep: (state) => state.stepper.loading,
}

const mutations = {
	updateField,
	setAircraftCategoryValue(state, category) {
		state.aircraftCategory = category
	},
	setLocationValue(state, { locationType, value }) {
		state.locations[locationType].value = value
	},
	clearLocationValue(state, locationType) {
		state.locations[locationType].value = null
		state.locations[locationType].transfer = {}
	},
	setDefaultDestinationLocationValue(state, value) {
		state.defaultDestinationLocation = value
	},
	setIsReturn(state, yesOrNo) {
		state.isReturn = yesOrNo
	},
	storeTransfer(state, { locationType, transfer }) {
		state.locations[locationType].transfer = transfer
	},
	storeTransferType(state, { locationType, transferType }) {
		state.locations[locationType].transferType = transferType
	},
	clearLocationTransfer(state, { locationType }) {
		state.locations[locationType].transfer = {}
	},
	setUuid(state, uuid) {
		state.uuid = uuid
	},
	setStepLoading(state, payload) {
		state.stepper.loading = payload
	},
}

const actions = {
	storeLocationValue({ commit }, payload) {
		commit('setLocationValue', payload)
	},
	deleteLocationValue({ commit }, payload) {
		commit('clearLocationValue', payload)
	},
	storeDefaultDestinationLocation({ commit }, payload) {
		commit('setDefaultDestinationLocationValue', payload)
	},	
	storeAircraftCategory({ commit }, payload) {
		commit('setAircraftCategoryValue', payload)
	},	

	/**
	 * Post main journey data to the API.
	 * Once successful, store the returned uuid and
	 * use it to fetch all available helicopters.
	 */
	submitJourney({ commit, dispatch }, payload) {
		const resource = "bookings/step-one"
		commit('operators/storeAircraftOptions', [], { root: true })
		ApiService.post(resource, payload)
			.then(response => {
				if (response.status === 200) {
					const uuid = response.data.flytUuid
					commit('setUuid', uuid)
					dispatch('operators/fetchHelicopters', uuid, { root: true })
				}
			})
			.catch(function (error) { console.log(error) })
	}
}

export default { namespaced: true, state, getters, mutations, actions }
